import { isDevMode } from '@angular/core';

import { JsonSchema } from '../model/json-schema';
import { SchemaNode } from '../model/schema-node.types';
import { SchemaReference, SchemaReferenceLoad } from '../model/schema-reference.types';
import { SchemaDocumentLoader } from './schema-document-loader.token';

/**
 * What a document carries beside its root, besides its `$schema`: it stays with the document. A document from before
 * 671070 may still carry an `id`.
 */
const DOCUMENT_KEYS = new Set(['version', 'id']);

/**
 * Where no app provides one, a reference that loads fails its load — no part vanishes without a word, whether the
 * page's loader asks for it or the node outlet does.
 */
export const NO_DOCUMENT_LOADER: SchemaDocumentLoader = {
  load: () => Promise.reject(new Error('no SCHEMA_DOCUMENT_LOADER is provided where this schema loads')),
};

/** Where a reference cannot wait for its part, named for the dev warning. */
interface PlaceThatCannotWait {
  readonly label: string;

  /** It renders once per row, and so does everything below it: no reference anywhere inside may wait either. */
  readonly perRow: boolean;
}

const DOCUMENT_ROOT: PlaceThatCannotWait = { label: 'the root of a document', perRow: false };

const REPEATED_ROW: PlaceThatCannotWait = { label: 'a repeated row (config.item)', perRow: true };

const TABLE_CELL: PlaceThatCannotWait = { label: 'a table cell (config.columns[].cell)', perRow: true };

const TABS_SLOT: PlaceThatCannotWait = { label: 'a tabs slot (config.slots.tabs)', perRow: false };

const LIST_CONTROLS_ITEM: PlaceThatCannotWait = { label: 'an item of list-controls (config.items)', perRow: false };

/** One composition: the documents it asked for, each key once, and the references it met where they cannot wait. */
interface Composer {
  /** The objects standing where a reference cannot wait for its part, with the place each stands in. */
  readonly cannotWait: Map<object, PlaceThatCannotWait>;

  /** `perRow` names the row or cell `value` renders in, once per row, so no reference inside it may wait. */
  compose(value: unknown, chain: readonly string[], perRow?: string): Promise<unknown>;

  resolve(reference: SchemaReference, chain: readonly string[], perRow?: string): Promise<unknown>;
}

/**
 * Whether `value` stands for another document: an object naming one by a string `$ref`. A node naming its own
 * `componentType` is written out, whatever else it carries — the blueprints reject the mix, and the renderer renders
 * what the node says it is.
 */
export function isSchemaReference(value: unknown): value is SchemaReference {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    typeof (value as { $ref?: unknown }).$ref === 'string' &&
    typeof (value as { componentType?: unknown }).componentType !== 'string'
  );
}

/**
 * The root of `document` with the place keys of `reference` applied, a place key winning over a root key of the same
 * name. Keys starting with `$` never reach the node, from either side, and the document's `version` stays with the
 * document, as does an `id` a document from before 671070 may still carry (README-TECH §16).
 */
export function applySchemaReference(reference: SchemaReference, document: JsonSchema): SchemaNode {
  const root = Object.entries(document).filter(([key]) => !key.startsWith('$') && !DOCUMENT_KEYS.has(key));
  const place = Object.entries(reference).filter(([key]) => !key.startsWith('$'));

  return Object.fromEntries([...root, ...place]) as unknown as SchemaNode;
}

/**
 * `tree` with every eager reference replaced by the root of the document it names, its place keys applied and its own
 * eager references composed in turn, at any depth. A lazy or preload reference stays as written, for the node outlet,
 * unless the loader forces every reference eager, or unless it stands where a reference cannot wait (670359): at the
 * root of `tree`, anywhere in a repeater's row (`config.item`) or a table cell (`config.columns[].cell`), which render
 * once per row, in a `tabs` slot (`config.slots.tabs`) or among the items of `list-controls`. There it loads now, with
 * the document that holds it, and dev mode warns.
 *
 * A preload reference's part starts resolving at once, its own eager references with it, and the tree does not wait
 * for it: a loader that keeps documents (`keepsDocuments`) hands the part to the node outlet later (672942). With any
 * other loader a preload reference loads now, as an eager one does, and dev mode warns: preloading would request it
 * twice.
 *
 * Each key loads once per call, and siblings load together. A reference back to a document above it rejects as a
 * cycle, and a part that fails to load rejects the whole tree, naming the chain of references that led to it. A branch
 * holding no reference comes back as the very object it was, so a tree without one is returned as it is. Every load gets
 * `abortSignal`, so the document loader can cancel the parts of a load that no longer matters.
 */
export function composeSchema<T>(tree: T, documentLoader: SchemaDocumentLoader, abortSignal?: AbortSignal): Promise<T> {
  const composer = composerOf(documentLoader, abortSignal);

  if (typeof tree === 'object' && tree !== null) {
    composer.cannotWait.set(tree, DOCUMENT_ROOT);
  }

  return composer.compose(tree, []) as Promise<T>;
}

/**
 * `reference` resolved now, whatever its `$refLoad`: the root of the document it names, with its place keys applied and
 * the document's own references composed as `composeSchema` composes a page's. `chainAbove` holds the keys of the
 * documents above the reference, so a reference back to one of them rejects as a cycle, naming the chain. The node
 * outlet's step for a reference it meets (670359).
 */
export function resolveSchemaReference(
  reference: SchemaReference,
  documentLoader: SchemaDocumentLoader,
  chainAbove: readonly string[],
  abortSignal?: AbortSignal
): Promise<SchemaNode> {
  return composerOf(documentLoader, abortSignal).resolve(reference, chainAbove) as Promise<SchemaNode>;
}

function composerOf(documentLoader: SchemaDocumentLoader, abortSignal: AbortSignal | undefined): Composer {
  const loaded = new Map<string, Promise<JsonSchema>>();
  const cannotWait = new Map<object, PlaceThatCannotWait>();

  const load = (key: string, chain: readonly string[]): Promise<JsonSchema> => {
    const known = loaded.get(key);

    if (known) {
      return known;
    }

    const failure = (reason: string, cause?: unknown): Error =>
      new Error(`Schema "${[...chain, key].join(' → ')}" could not be loaded: ${reason}`, { cause });

    // A loader written as `Promise.resolve(documents[key])` answers an unknown key with nothing instead of rejecting.
    const loading = documentLoader.load(key, abortSignal).then(
      (document: unknown) => {
        if (typeof document !== 'object' || document === null || Array.isArray(document)) {
          throw failure(`the document loader answered "${key}" with no document`);
        }

        return document as JsonSchema;
      },
      (cause: unknown) => {
        throw failure(messageOf(cause), cause);
      }
    );
    loaded.set(key, loading);

    return loading;
  };

  const resolve = async (reference: SchemaReference, chain: readonly string[], perRow?: string): Promise<unknown> => {
    if (chain.includes(reference.$ref)) {
      throw new Error(`Schema reference cycle: ${[...chain, reference.$ref].join(' → ')}`);
    }

    return compose(applySchemaReference(reference, await load(reference.$ref, chain)), [...chain, reference.$ref], perRow);
  };

  const compose = async (value: unknown, chain: readonly string[], perRow?: string): Promise<unknown> => {
    if (isSchemaReference(value)) {
      const place = cannotWait.get(value);
      const mode = value.$refLoad;

      if ((mode === 'lazy' || mode === 'preload') && !documentLoader.forceEager) {
        const label = place?.label ?? perRow;

        if (label !== undefined) {
          warnCannotWait(value.$ref, mode, label);
        } else if (mode === 'lazy') {
          return value;
        } else if (documentLoader.keepsDocuments) {
          // A part that fails to preload fails again once its node shows, and the node outlet reports it there.
          void resolve(value, chain).catch(() => undefined);

          return value;
        } else {
          warnKeepsNoDocuments(value.$ref);
        }
      }

      return resolve(value, chain, place?.perRow ? place.label : perRow);
    }

    if (Array.isArray(value)) {
      const items = await Promise.all(value.map((item) => compose(item, chain, perRow)));

      return items.every((item, index) => item === value[index]) ? value : items;
    }

    if (value === null || typeof value !== 'object') {
      return value;
    }

    const place = cannotWait.get(value);
    const below = place?.perRow ? place.label : perRow;

    markPlacesThatCannotWait(value as Record<string, unknown>, cannotWait);

    const entries = await Promise.all(Object.entries(value).map(async ([key, item]) => [key, await compose(item, chain, below)] as const));

    return entries.every(([key, item]) => item === (value as Record<string, unknown>)[key]) ? value : Object.fromEntries(entries);
  };

  return { cannotWait, compose, resolve };
}

/**
 * Where a reference cannot wait for its part (670359). A repeater builds its rows from its `item` once, with no outlet,
 * and a table renders each column's `cell` in every row, so a part waiting anywhere inside either would be fetched once
 * per row. A tile's tab provider reads the config of its `tabs` slot, and `list-controls` resolves its items itself, so
 * no outlet could load those first. Records each such child of `node` with its place.
 */
function markPlacesThatCannotWait(node: Record<string, unknown>, cannotWait: Map<object, PlaceThatCannotWait>): void {
  if (typeof node['componentType'] !== 'string') {
    return;
  }

  const config = node['config'] as { item?: unknown; columns?: unknown; slots?: { tabs?: unknown }; items?: unknown } | undefined;
  const columns = config?.columns;
  const items = node['componentType'] === 'list-controls' ? config?.items : undefined;
  const places: [unknown, PlaceThatCannotWait][] = [
    [config?.item, REPEATED_ROW],
    ...(Array.isArray(columns)
      ? columns.map((column): [unknown, PlaceThatCannotWait] => [(column as { cell?: unknown } | null)?.cell, TABLE_CELL])
      : []),
    [config?.slots?.tabs, TABS_SLOT],
    ...(Array.isArray(items) ? items.map((item): [unknown, PlaceThatCannotWait] => [item, LIST_CONTROLS_ITEM]) : []),
  ];

  for (const [child, place] of places) {
    if (typeof child === 'object' && child !== null) {
      cannotWait.set(child, place);
    }
  }
}

function warnCannotWait(key: string, mode: SchemaReferenceLoad, place: string): void {
  if (isDevMode()) {
    console.warn(
      `Schema reference "${key}" with "$refLoad": "${mode}" stands in ${place}, where a part cannot wait, ` +
        'so it loads with the document that holds it.'
    );
  }
}

function warnKeepsNoDocuments(key: string): void {
  if (isDevMode()) {
    console.warn(
      `Schema reference "${key}" with "$refLoad": "preload" loads with the document that holds it: ` +
        'its loader keeps no documents, so preloading would request it twice.'
    );
  }
}

/** An `HttpErrorResponse` carries a `message` without being an `Error`, and `String()` on it says only `[object Object]`. */
function messageOf(cause: unknown): string {
  const message = (cause as { message?: unknown } | null)?.message;

  return typeof message === 'string' ? message : String(cause);
}
